import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { generateApplicationToken } from '../../../redux/features/developer/actions';
import { showMessage } from '../../../redux/features/messaging/actions';
import { useAppSelector } from '../../../redux/hooks';

/**
 * Determines how the token's lifetime value is interpreted.
 *
 * - `'hours'`     – The lifetime value is treated as a number of hours.
 * - `'days'`      – The lifetime value is treated as a number of days.
 * - `'long-form'` – A fixed ~1-year lifetime ({@link LONG_FORM_DAYS}) is used;
 *                   no custom value is required from the user.
 */
type TLifetimeMode = 'hours' | 'days' | 'long-form';

/**
 * Identifies which user or service application the token should be minted for.
 *
 * - `'current'`   – Use the currently logged-in user's ID (no manual entry needed).
 * - `'platform'`  – Platform service account.
 * - `'supersync'` – SuperSync service account.
 * - `'superlift'` – SuperLift service account.
 * - `'custom'`    – Manually enter any Rentsync user ID.
 */
type TUserOption = 'current' | 'platform' | 'supersync' | 'superlift' | 'custom';

/** Display labels and option values rendered in the User / Application dropdown. */
const USER_OPTIONS: { label: string; value: TUserOption }[] = [
  { label: 'Current User', value: 'current' },
  { label: 'Platform', value: 'platform' },
  { label: 'SuperSync', value: 'supersync' },
  { label: 'SuperLift', value: 'superlift' },
  { label: 'Custom...', value: 'custom' },
];

/** Number of days used for the "long-form" (~1 year) token lifetime option. */
const LONG_FORM_DAYS = 365; // ~1 year

/**
 * Developer Control Panel page that lets authorised developers mint application
 * tokens on behalf of a Rentsync user.
 *
 * The component allows the operator to specify:
 * - **User / Application** – a dropdown selecting whose token to generate:
 *   Current User, Platform, SuperSync, SuperLift, or a Custom user ID.
 *   A free-text User ID field is shown for all non-current-user selections.
 * - **Token Lifetime** – expressed in hours, days, or as a long-form (~1 year)
 *   token.
 *
 * On success, the generated JWT is displayed in a read-only textarea with a
 * one-click copy-to-clipboard action.
 *
 * @example
 * // Rendered automatically at /developer/token_generator by Developer.tsx
 * <TokenGenerator />
 */
export const TokenGenerator = () => {
  const loggedInUserId = useAppSelector((state) => String(state.auth?.decodedToken?.userId ?? ''));

  const [userOption, setUserOption] = useState<TUserOption>('current');
  const [serviceUserId, setServiceUserId] = useState<string>('');
  const [lifetimeMode, setLifetimeMode] = useState<TLifetimeMode>('hours');
  const [lifetimeValue, setLifetimeValue] = useState<string>('8');
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const resolvedUserId = userOption === 'current' ? loggedInUserId : serviceUserId;

  /**
   * Validates inputs, calls {@link generateApplicationToken}, and stores the
   * resulting token in local state. Shows a snackbar on both success and
   * validation failure.
   */
  const handleGenerate = async () => {
    if (!resolvedUserId) {
      showMessage({ message: 'User ID is required.', severity: 'error', asAlert: false });
      return;
    }

    setIsLoading(true);
    setGeneratedToken(null);

    const params =
      lifetimeMode === 'long-form'
        ? { userId: resolvedUserId, lifetimeDays: String(LONG_FORM_DAYS) }
        : lifetimeMode === 'days'
        ? { userId: resolvedUserId, lifetimeDays: lifetimeValue }
        : { userId: resolvedUserId, lifetimeHours: lifetimeValue };

    const token = await generateApplicationToken(params);
    setIsLoading(false);

    if (token) {
      setGeneratedToken(token);
      showMessage({ message: 'Token generated successfully!', severity: 'success', asAlert: false });
    }
  };

  /**
   * Copies the currently displayed token to the system clipboard and shows a
   * confirmation snackbar. No-ops when no token has been generated yet.
   */
  const handleCopy = () => {
    if (!generatedToken) return;
    navigator.clipboard.writeText(generatedToken);
    showMessage({ message: 'Token copied to clipboard!', severity: 'success', asAlert: false });
  };

  return (
    <Grid container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generate Application Token
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={userOption !== 'current' ? 6 : 12}>
                <FormControl fullWidth size="small">
                  <InputLabel>User / Application</InputLabel>
                  <Select
                    value={userOption}
                    label="User / Application"
                    onChange={(e) => {
                      setUserOption(e.target.value as TUserOption);
                      setServiceUserId('');
                    }}
                  >
                    {USER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {userOption !== 'current' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="User ID"
                    value={serviceUserId}
                    onChange={(e) => setServiceUserId(e.target.value)}
                    helperText="Enter the Rentsync user ID for this application"
                    size="small"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Token Lifetime</InputLabel>
                  <Select
                    value={lifetimeMode}
                    label="Token Lifetime"
                    onChange={(e) => {
                      setLifetimeMode(e.target.value as TLifetimeMode);
                      if (e.target.value === 'hours') setLifetimeValue('8');
                      else if (e.target.value === 'days') setLifetimeValue('1');
                    }}
                  >
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="long-form">Long-form (~1 year)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {lifetimeMode !== 'long-form' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={lifetimeMode === 'hours' ? 'Hours' : 'Days'}
                    type="number"
                    value={lifetimeValue}
                    onChange={(e) => setLifetimeValue(e.target.value)}
                    inputProps={{ min: 1 }}
                    size="small"
                  />
                </Grid>
              )}

              {lifetimeMode === 'long-form' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Days"
                    value={LONG_FORM_DAYS}
                    size="small"
                    disabled
                    helperText="Approx. 1 year"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerate}
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating…' : 'Generate Token'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {generatedToken && (
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Generated Token</strong>
                <FontAwesomeIcon
                  icon={faCopy as IconProp}
                  style={{ marginLeft: '10px', cursor: 'pointer' }}
                  onClick={handleCopy}
                  title="Copy to clipboard"
                />
              </Typography>
              <Box
                component="textarea"
                readOnly
                value={generatedToken}
                sx={{
                  width: '100%',
                  minHeight: '100px',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  border: '1px solid #CCC',
                  padding: '8px',
                  resize: 'vertical',
                  wordBreak: 'break-all',
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};
